// Sala pelo link: abre a conexão com a mesa e mostra a sala de espera ou a partida.
import { ligaNoAr } from '../comum/liga.ts';
import { useEffect, useRef, useState } from 'react';
import type { OrigemDaVaga, Perfil } from '@truco6/mesa/protocolo';
import type { Seat } from '@truco6/regras';
import { pode } from '@truco6/planos';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { config } from '../comum/config.ts';
import { medirUmaVez } from '../comum/medicao.ts';
import { Palco } from '../comum/Palco.tsx';
import { ir } from '../comum/rotas.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { atualizarLimite } from '../comum/limite.ts';
import { useMesa } from '../comum/useMesa.ts';
import { ConexaoMesa } from '../mesa/conexao.ts';
import { lerEu, lerPoteDaPartida, lerTentos } from '../comum/api.ts';
import { Contagem } from '../mesa/Contagem.tsx';
import { Mesa } from '../mesa/Mesa.tsx';
import { vagasAbertas } from '../mesa/textos.ts';
import { FolhaCelular } from '../comum/FolhaCelular.tsx';
import { FolhaChamarAmigos } from './Amigos.tsx';
import { Espera } from './Espera.tsx';
import { irParaCheckout } from './planos-comum.tsx';
import { GuardarConta } from './GuardarConta.tsx';
import { useSemPartidas } from '../comum/SeloGratis.tsx';
import { Marca } from '@truco6/ui/Marca';
import { Carregando, Tela } from './comum.tsx';

const ERROS: Record<string, string> = {
  sala_inexistente: 'Essa sala não existe mais. Peça um link novo para quem chamou.',
  sala_cheia: 'A sala está cheia.',
  token_invalido: 'Sua sessão venceu. Entre de novo.',
  outra_conexao: 'Você abriu esta mesa em outra aba ou aparelho.',
  sala_encerrada: 'O dono saiu e a sala foi encerrada.',
};

// O que a mesa vê do perfil. Os gritos gravados vão só com plano que tem o recurso e com consentimento.
function perfilNaMesa(p: PerfilConta, s: Sessao): Perfil {
  const gritos = !s.anonimo && pode(p.plano, 'gravarGritos') && p.vozConsentidaEm && Object.keys(p.gritos).length ? p.gritos : undefined;
  return gritos ? { apelido: p.apelido, avatar: p.avatar, gritos } : { apelido: p.apelido, avatar: p.avatar };
}

// vaga: chegou para sentar na vaga aberta (pela lista de salas ou pelo chamado de um amigo).
export function Sala({ codigo, vaga, sessao, perfil }: { codigo: string; vaga?: OrigemDaVaga; sessao: Sessao; perfil: PerfilConta }) {
  const [c, setC] = useState<ConexaoMesa | null>(null);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    const x = new ConexaoMesa(config.mesaUrl, () => sessao.token(), perfilNaMesa(perfil, sessao), codigo).iniciar();
    setC(x);
    return () => x.encerrar();
  }, [codigo, sessao.id, tentativa]);
  return c ? <SalaAberta c={c} sessao={sessao} vaga={vaga} denovo={() => setTentativa((n) => n + 1)} /> : <Carregando texto="Entrando na mesa..." />;
}

function SalaAberta({ c, sessao, vaga, denovo }: { c: ConexaoMesa; sessao: Sessao; vaga?: OrigemDaVaga; denovo: () => void }) {
  const e = useMesa(c);
  const [chamando, setChamando] = useState(false);
  // Por onde chegou à vaga: a rota diz (lista, amigo); senão, pelo link ou código se a vaga já estava aberta quando
  // entrou, ou assistindo, se abriu depois.
  const vagaNaChegada = useRef<boolean | null>(null);
  if (vagaNaChegada.current === null && e.sala) vagaNaChegada.current = e.sala.fase === 'jogo' && vagasAbertas(e.sala.lugares).length > 0;
  const origem: OrigemDaVaga = vaga ?? (vagaNaChegada.current ? 'link' : 'espectador');
  // Chegou para sentar: senta na primeira vaga aberta, uma vez. Sem vaga, avisa como o servidor avisa a corrida.
  const tentou = useRef(false);
  const [semVaga, setSemVaga] = useState(false);
  useEffect(() => {
    if (!vaga || tentou.current || !e.sala || e.eu !== null) return;
    // Chegou pelo convite (link da sala, depois do Google e do Criar perfil): na sala de espera, o primeiro lugar livre.
    if (e.sala.fase === 'espera' && vaga === 'link') {
      tentou.current = true;
      const livre = e.sala.lugares.findIndex((l) => l === null);
      if (livre >= 0) { c.enviar({ t: 'sentar', lugar: livre as Seat }); return; }
      c.encerrar();
      ir({ tela: 'inicio' }, true);
      avisarNaTela('A sala encheu. Escolha outra mesa aqui embaixo.', 6000);
      return;
    }
    if (e.sala.fase !== 'jogo') return;
    tentou.current = true;
    const s = vagasAbertas(e.sala.lugares)[0];
    if (s !== undefined) { c.enviar({ t: 'sentar', lugar: s, origem: vaga }); return; }
    if (vaga === 'link') { c.encerrar(); ir({ tela: 'inicio' }, true); avisarNaTela('A sala encheu. Escolha outra mesa aqui embaixo.', 6000); return; }
    setSemVaga(true);
  }, [e.sala, e.eu]);
  // Sentou pelo convite: o ?vaga=link sai do endereço, para recarregar a página ou levantar (Não na revanche) não
  // sentar de novo sozinho.
  useEffect(() => { if (vaga === 'link' && e.eu !== null && e.sala) ir({ tela: 'sala', codigo: e.sala.codigo }, true); }, [e.eu]);
  const emJogo = !!e.sala && e.sala.fase !== 'espera' && e.eu !== null && !!e.visao;
  const pessoas = e.sala?.lugares.filter((l) => l?.tipo === 'pessoa').length ?? 0;
  useEffect(() => {
    if (e.sala) medirUmaVez('sala:' + e.sala.codigo, 'entrar_sala', { dono: e.usuario === e.sala.dono });
  }, [e.sala?.codigo]);
  useEffect(() => {
    if (emJogo && e.sala) medirUmaVez(`partida:${e.sala.codigo}:${e.partida}`, 'partida_iniciada', { pessoas, bots: 6 - pessoas });
  }, [emJogo, e.partida]);
  // Sem partidas grátis hoje (a mesa recusou sentar, ou levantou antes de começar): a tela Limite explica o que fazer.
  // Quem chega pelo link, pelo código ou pela lista sem lugar na mesa também vai para lá; quem já está sentado (voltou
  // para a própria partida) segue jogando. Assistir continua liberado, pela plateia.
  const semPartidas = useSemPartidas();
  const chegouSemLugar = !!e.sala && e.eu === null && e.sala.fase !== 'encerrada' && !e.sala.campeonato;
  useEffect(() => { if (e.semPartidas || (semPartidas && chegouSemLugar)) { c.encerrar(); ir({ tela: 'limite' }, true); } }, [e.semPartidas, semPartidas, chegouSemLugar]);
  // Ao sair da mesa, o selo e o menu leem o limite de novo.
  useEffect(() => () => { void atualizarLimite(); }, []);
  // Conta Google sem celular: a mesa recusou sentar. Salvou, senta no mesmo lugar; "Agora não" deixa a pessoa fora da
  // mesa (na mesa do campeonato, que só tem lugar marcado, volta para onde estava).
  const folhaCelular = e.faltaCelular && (
    <FolhaCelular sessao={sessao} aoSalvar={() => c.sentarDeNovo()} aoFechar={() => {
      c.deixarParaDepois();
      if (!e.sala) { if (history.length > 1) history.back(); else ir({ tela: 'inicio' }); }
    }} />
  );
  // A conta anônima de antes, fora da partida em que estava sentada: a mesa pede a conta Google.
  if (e.erro?.codigo === 'conta_obrigatoria') return <GuardarConta />;
  if (e.status === 'encerrada' || (e.erro && !e.sala)) {
    const codigo = e.erro?.codigo ?? 'rede';
    // Mesa da liga que fechou (ou de outra pessoa): volta para a busca.
    const daLiga = !!e.sala?.liga || ((codigo === 'sem_permissao' || codigo === 'sala_encerrada') && !!e.erro?.mensagem.includes('liga'));
    return (
      <Tela>
        <Marca altura={40} />
        <p className="status" role="alert">{(daLiga ? e.erro?.mensagem : ERROS[codigo]) ?? e.erro?.mensagem ?? 'A conexão com a mesa caiu.'}</p>
        {daLiga && (ligaNoAr()
          ? <button className="btn1 largo" onClick={() => ir({ tela: 'liga', trio: null })}>JOGAR NA LIGA</button>
          : <button className="btn1 largo" onClick={() => ir({ tela: 'inicio' })}>VER SALAS ABERTAS</button>)}
        {codigo === 'outra_conexao' || codigo === 'rede'
          ? <button className="btn1 largo" onClick={denovo}>JOGAR AQUI</button>
          : null}
        {codigo === 'sem_tentos' && <button className="btn1 largo" onClick={() => ir({ tela: 'loja' })}>COMPRAR TENTOS</button>}
        <button className="btn2" onClick={() => ir({ tela: 'inicio' })}>Voltar ao início</button>
      </Tela>
    );
  }
  if (!e.sala) return <><Carregando texto={e.status === 'reconectando' ? 'Reconectando à mesa...' : 'Entrando na mesa...'} />{folhaCelular}</>;
  // A contagem da revanche fica por cima de tudo: da sala de espera durante o 3, 2, 1 e da mesa no VALENDO!.
  if (emJogo) {
    return (
      <>
        <Palco computador>{(paisagem, modo) => (
          <>
            <Mesa c={c} paisagem={paisagem} modo={modo} lerSaldo={sessao.anonimo ? undefined : () => lerTentos(sessao).then((p) => p.saldo)}
              lerPote={sessao.anonimo ? undefined : (codigo, partida) => lerPoteDaPartida(sessao, codigo, partida)}
              lerConvite={sessao.anonimo ? undefined : () => lerEu(sessao).then((x) => x.indicacao?.link ?? null)}
              assinar={sessao.anonimo ? undefined : () => irParaCheckout(sessao, 'bronze', 'mensal', 'mesa', true)}
              aoChamarAmigos={sessao.anonimo ? undefined : () => setChamando(true)} />
            {chamando && <FolhaChamarAmigos sessao={sessao} aoFechar={() => setChamando(false)} />}
          </>
        )}</Palco>
        <Contagem e={e} />
        {folhaCelular}
      </>
    );
  }
  return (
    <>
      <Espera c={c} sessao={sessao} origem={origem} recado={semVaga ? 'Alguém sentou antes. Escolha outra sala.' : null} />
      <Contagem e={e} />
      {folhaCelular}
    </>
  );
}
